<template>
    <div class="bo-wrap h-full w-full flex flex-col text-left">
        <div class="bo-toolbar">
            <p class="bo-toolbar__hint">{{ entity }} · {{ campus }}</p>
            <div class="bo-toolbar__actions">
                <button
                    type="button"
                    class="bo-btn bo-btn--export"
                    :disabled="isExportExcel || loading || !record"
                    @click="generateReport"
                >
                    <font-awesome-icon :icon="['fas', 'file-excel']" class="bo-btn__icon bo-btn__icon--excel" />
                    <span>{{ isExportExcel ? "Exporting..." : "Export Excel" }}</span>
                </button>
                <button
                    v-if="canCreate && !record"
                    type="button"
                    class="bo-btn bo-btn--edit"
                    :disabled="loading"
                    @click="openCreate"
                >
                    <font-awesome-icon icon="plus" class="bo-btn__icon" />
                    <span>Add</span>
                </button>
                <button
                    v-if="canUpdate && record"
                    type="button"
                    class="bo-btn bo-btn--edit"
                    :disabled="loading"
                    @click="openEdit"
                >
                    <font-awesome-icon icon="pen" class="bo-btn__icon" />
                    <span>Edit</span>
                </button>
            </div>
        </div>

        <div class="bo-body flex-auto flex flex-col">
            <div v-if="loading" class="flex-auto flex justify-center items-center py-24">
                <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
            </div>

            <div v-else-if="!record" class="flex-auto flex justify-center items-center py-20">
                <div class="bo-empty">
                    <div class="bo-empty__glyph">
                        <font-awesome-icon :icon="['fas', 'expand']" />
                    </div>
                    <h2 class="bo-empty__title">No campus areas found</h2>
                    <p class="bo-empty__text">
                        No landscape / building area record exists for this campus yet.
                    </p>
                    <button
                        v-if="canCreate"
                        type="button"
                        class="bo-btn bo-btn--edit bo-empty__action"
                        @click="openCreate"
                    >
                        <font-awesome-icon icon="plus" class="bo-btn__icon" />
                        <span>Add campus areas</span>
                    </button>
                </div>
            </div>

            <div v-else class="bo-content">
                <div class="bo-metrics bo-metrics--areas">
                    <article
                        v-for="(item, index) in displayItems"
                        :key="item.key"
                        class="bo-feature bo-anim"
                        :class="'bo-feature--' + item.accent"
                        :style="{ '--bo-delay': index * 60 + 'ms' }"
                    >
                        <div class="bo-feature__icon" aria-hidden="true">
                            <font-awesome-icon :icon="['fas', item.icon]" />
                        </div>
                        <div class="bo-feature__copy min-w-0">
                            <p class="bo-feature__label">{{ item.title }}</p>
                            <p class="bo-feature__value bo-feature__value--lg">
                                {{ formatArea(item.value) }}
                                <span class="bo-feature__unit">{{ record.unit || "m²" }}</span>
                            </p>
                        </div>
                    </article>
                </div>

                <article class="bo-feature bo-feature--unit bo-anim" style="--bo-delay: 240ms">
                    <div class="bo-feature__icon" aria-hidden="true">
                        <font-awesome-icon :icon="['fas', 'layer-group']" />
                    </div>
                    <div class="bo-feature__copy min-w-0">
                        <p class="bo-feature__label">Unit of measure</p>
                        <p class="bo-feature__value">{{ record.unit || "m²" }}</p>
                    </div>
                </article>
            </div>
        </div>

        <transition name="fade">
            <div
                v-if="showFormModal"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                @click.self="closeForm"
            >
                <div class="bo-form bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
                    <div class="bo-form__header">
                        <h3 class="text-lg font-semibold text-gray-900">
                            {{ isCreating ? "Add campus areas" : "Edit campus areas" }}
                        </h3>
                        <p class="text-sm text-gray-500 mt-1">
                            {{ entity }} · {{ campus }}
                        </p>
                    </div>

                    <div class="bo-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label class="bo-label">Landscape Area</label>
                            <input
                                v-model="form.landscape_area"
                                type="number"
                                min="0"
                                step="0.001"
                                class="bo-input"
                                placeholder="Area in m²"
                            />
                        </div>
                        <div>
                            <label class="bo-label">Main Building</label>
                            <input
                                v-model="form.main_building"
                                type="number"
                                min="0"
                                step="0.001"
                                class="bo-input"
                                placeholder="Area in m²"
                            />
                        </div>
                        <div>
                            <label class="bo-label">Gym Building</label>
                            <input
                                v-model="form.gym_building"
                                type="number"
                                min="0"
                                step="0.001"
                                class="bo-input"
                                placeholder="Area in m²"
                            />
                        </div>
                        <div>
                            <label class="bo-label">Students Center Building</label>
                            <input
                                v-model="form.students_center_building"
                                type="number"
                                min="0"
                                step="0.001"
                                class="bo-input"
                                placeholder="Area in m²"
                            />
                        </div>
                        <div class="sm:col-span-2">
                            <label class="bo-label">Unit</label>
                            <input
                                v-model="form.unit"
                                type="text"
                                class="bo-input"
                                placeholder="m²"
                            />
                        </div>
                    </div>

                    <div class="bo-form__footer">
                        <button type="button" class="bo-btn bo-btn--ghost" @click="closeForm">
                            Cancel
                        </button>
                        <base-button
                            type="button"
                            :disabled="saving"
                            class="bg-amber-700 hover:bg-amber-800 text-white"
                            @click="saveForm"
                        >
                            {{ saving ? "Saving…" : "Save" }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import exportManagerMixin from "./exportManagerMixin.js";

var AREA_FIELDS = [
    { key: "landscape_area", title: "Landscape Area", icon: "leaf", accent: "teal" },
    { key: "main_building", title: "Main Building", icon: "building", accent: "amber" },
    { key: "gym_building", title: "Gym Building", icon: "dumbbell", accent: "orange" },
    {
        key: "students_center_building",
        title: "Students Center Building",
        icon: "users",
        accent: "stone",
    },
];

const LIST_QUERY = gql`
    query FacilityAreas(
        $first: Int!
        $page: Int!
        $entity: String
        $campus: String
    ) {
        facilityAreas(
            first: $first
            page: $page
            entity: $entity
            campus: $campus
        ) {
            error
            error_code
            message
            data {
                id
                entity
                campus
                landscape_area
                main_building
                gym_building
                students_center_building
                unit
                created_at
                updated_at
            }
        }
    }
`;

const CREATE_MUTATION = gql`
    mutation CreateFacilityArea($input: FacilityAreaInput!) {
        createFacilityArea(input: $input) {
            error
            error_code
            message
        }
    }
`;

const UPDATE_MUTATION = gql`
    mutation UpdateFacilityArea($id: ID!, $input: FacilityAreaInput!) {
        updateFacilityArea(id: $id, input: $input) {
            error
            error_code
            message
        }
    }
`;

function emptyForm() {
    return {
        landscape_area: "",
        main_building: "",
        gym_building: "",
        students_center_building: "",
        unit: "m²",
    };
}

function parseAreaValue(value) {
    if (value === "" || value === null || value === undefined) return null;
    var num = parseFloat(value);
    if (isNaN(num) || num < 0) return null;
    return num;
}

function formatArea(value) {
    if (value === null || value === undefined || value === "") return "—";
    return Number(value).toLocaleString("en-US");
}

export default {
    name: "FacilityAreasModule",
    mixins: [exportManagerMixin],
    props: {
        entity: { type: String, required: true },
        campus: { type: String, required: true },
    },
    emits: ["changed"],
    data() {
        return {
            record: null,
            loading: false,
            isExportExcel: false,
            exportSingleRecord: true,
            showFormModal: false,
            isCreating: false,
            saving: false,
            form: emptyForm(),
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        canCreate() {
            return this.hasPermission("facility-areas-create");
        },
        canUpdate() {
            return this.hasPermission("facility-areas-update");
        },
        displayItems() {
            var record = this.record;
            if (!record) return [];
            return AREA_FIELDS.map(function (field) {
                return {
                    key: field.key,
                    title: field.title,
                    icon: field.icon,
                    accent: field.accent,
                    value: record[field.key],
                };
            });
        },
    },
    created() {
        this.loadRecord();
    },
    methods: {
        formatArea: formatArea,
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        toastAlert(options) {
            if (!this.$swal) return Promise.resolve({});
            var merged = Object.assign({ timer: 2000 }, options);
            if (
                (merged.icon === "error" || merged.icon === "warning") &&
                (options.timer === undefined || options.timer === null)
            ) {
                merged.timer = 3500;
            }
            var self = this;
            var Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timerProgressBar: true,
                didOpen: function (toast) {
                    toast.addEventListener("mouseenter", self.$swal.stopTimer);
                    toast.addEventListener("mouseleave", self.$swal.resumeTimer);
                },
            });
            return Toast.fire(merged);
        },
        loadRecord() {
            var self = this;
            this.loading = true;
            this.$apollo
                .query({
                    query: LIST_QUERY,
                    variables: {
                        first: 1,
                        page: 1,
                        entity: this.entity,
                        campus: this.campus,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var data = result && result.data ? result.data : null;
                    var payload = data && data.facilityAreas ? data.facilityAreas : null;
                    if (payload && !payload.error) {
                        var rows = payload.data || [];
                        self.record = rows.length ? rows[0] : null;
                    } else {
                        self.record = null;
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Failed to load campus areas",
                        });
                    }
                })
                .catch(function (err) {
                    self.record = null;
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Failed to load campus areas",
                    });
                })
                .finally(function () {
                    self.loading = false;
                });
        },
        openCreate() {
            if (this.record) return;
            this.isCreating = true;
            this.form = emptyForm();
            this.showFormModal = true;
        },
        openEdit() {
            if (!this.record) return;
            var item = this.record;
            this.isCreating = false;
            this.form = {
                landscape_area:
                    item.landscape_area === null || item.landscape_area === undefined
                        ? ""
                        : item.landscape_area,
                main_building:
                    item.main_building === null || item.main_building === undefined
                        ? ""
                        : item.main_building,
                gym_building:
                    item.gym_building === null || item.gym_building === undefined
                        ? ""
                        : item.gym_building,
                students_center_building:
                    item.students_center_building === null ||
                    item.students_center_building === undefined
                        ? ""
                        : item.students_center_building,
                unit: item.unit || "m²",
            };
            this.showFormModal = true;
        },
        closeForm() {
            this.showFormModal = false;
            this.isCreating = false;
            this.form = emptyForm();
        },
        buildInput() {
            return {
                entity: this.entity,
                campus: this.campus,
                landscape_area: parseAreaValue(this.form.landscape_area),
                main_building: parseAreaValue(this.form.main_building),
                gym_building: parseAreaValue(this.form.gym_building),
                students_center_building: parseAreaValue(this.form.students_center_building),
                unit: this.form.unit || "m²",
            };
        },
        saveForm() {
            var self = this;

            if (!this.isCreating && (!this.record || !this.record.id)) {
                this.toastAlert({ icon: "error", title: "No record selected to update" });
                return;
            }

            if (this.isCreating && this.record) {
                this.toastAlert({
                    icon: "warning",
                    title: "Campus areas already exist for this campus",
                });
                return;
            }

            var input = this.buildInput();
            if (
                input.landscape_area === null &&
                input.main_building === null &&
                input.gym_building === null &&
                input.students_center_building === null
            ) {
                this.toastAlert({ icon: "warning", title: "Enter at least one area value" });
                return;
            }

            this.saving = true;
            var request = this.isCreating
                ? this.$apollo.mutate({
                      mutation: CREATE_MUTATION,
                      variables: { input: input },
                  })
                : this.$apollo.mutate({
                      mutation: UPDATE_MUTATION,
                      variables: { id: this.record.id, input: input },
                  });

            request
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload = self.isCreating
                        ? data && data.createFacilityArea
                            ? data.createFacilityArea
                            : null
                        : data && data.updateFacilityArea
                          ? data.updateFacilityArea
                          : null;

                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Saved successfully",
                        });
                        self.closeForm();
                        self.loadRecord();
                        self.$emit("changed", { refresh: true });
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Save failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Save failed",
                    });
                })
                .finally(function () {
                    self.saving = false;
                });
        },
        exportFileName: function () {
            return "ADPOLY_" + this.campus + "_Campus_Areas";
        },
        exportSheetName: function () {
            return "Campus Areas";
        },
        buildExportRows: function () {
            if (!this.record) return [];
            var unit = this.record.unit || "m²";
            return [
                {
                    "Landscape Area":
                        this.record.landscape_area === null ||
                        this.record.landscape_area === undefined
                            ? ""
                            : this.record.landscape_area,
                    "Main Building":
                        this.record.main_building === null ||
                        this.record.main_building === undefined
                            ? ""
                            : this.record.main_building,
                    "Gym Building":
                        this.record.gym_building === null ||
                        this.record.gym_building === undefined
                            ? ""
                            : this.record.gym_building,
                    "Students Center Building":
                        this.record.students_center_building === null ||
                        this.record.students_center_building === undefined
                            ? ""
                            : this.record.students_center_building,
                    Unit: unit,
                },
            ];
        },
    },
};
</script>

<style lang="scss" scoped>
.bo-wrap {
    --bo-ink: #1c1917;
    --bo-muted: #78716c;
    --bo-line: #e7e5e4;
    --bo-amber: #b45309;
    --bo-amber-soft: #fff7ed;
    background:
        radial-gradient(1200px 420px at 10% -10%, rgba(251, 191, 36, 0.16), transparent 55%),
        radial-gradient(900px 380px at 100% 0%, rgba(15, 118, 110, 0.08), transparent 50%),
        linear-gradient(180deg, #fafaf9 0%, #ffffff 48%);
}

.bo-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1.15rem 1.25rem 0.35rem;
}

@media (min-width: 640px) {
    .bo-toolbar {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}

.bo-toolbar__hint {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--bo-muted);
}

.bo-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}

.bo-body {
    padding: 0.75rem 1.25rem 2rem;
}

@media (min-width: 640px) {
    .bo-body {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}

.bo-content {
    max-width: 56rem;
    margin: 0 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.bo-metrics--areas {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
}

@media (min-width: 640px) {
    .bo-metrics--areas {
        grid-template-columns: 1fr 1fr;
    }
}

.bo-feature {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem 1.35rem;
    border-radius: 1.15rem;
    border: 1px solid var(--bo-line);
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 1px 2px rgba(28, 25, 23, 0.04);
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.bo-feature:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(28, 25, 23, 0.08);
    border-color: #d6d3d1;
}

.bo-feature__icon {
    flex-shrink: 0;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    color: #fff;
}

.bo-feature--teal .bo-feature__icon {
    background: linear-gradient(145deg, #14b8a6, #0f766e);
}

.bo-feature--amber .bo-feature__icon {
    background: linear-gradient(145deg, #d97706, #b45309);
}

.bo-feature--orange .bo-feature__icon {
    background: linear-gradient(145deg, #fb923c, #c2410c);
}

.bo-feature--stone .bo-feature__icon {
    background: linear-gradient(145deg, #78716c, #44403c);
}

.bo-feature--unit .bo-feature__icon {
    background: linear-gradient(145deg, #0d9488, #115e59);
}

.bo-feature__label {
    margin: 0;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--bo-muted);
}

.bo-feature__value {
    margin: 0.35rem 0 0;
    font-size: 1.05rem;
    font-weight: 650;
    line-height: 1.45;
    color: var(--bo-ink);
    word-break: break-word;
}

.bo-feature__value--lg {
    font-size: 1.55rem;
    font-weight: 750;
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.bo-feature__unit {
    margin-left: 0.25rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--bo-muted);
}

.bo-anim {
    animation: boRise 0.45s ease both;
    animation-delay: var(--bo-delay, 0ms);
}

@keyframes boRise {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.bo-empty {
    text-align: center;
    max-width: 22rem;
    padding: 0 1rem;
}

.bo-empty__glyph {
    width: 3.5rem;
    height: 3.5rem;
    margin: 0 auto 1rem;
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bo-amber-soft);
    color: var(--bo-amber);
    font-size: 1.35rem;
}

.bo-empty__title {
    margin: 0 0 0.4rem;
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--bo-ink);
}

.bo-empty__text {
    margin: 0;
    color: var(--bo-muted);
    font-size: 0.95rem;
    line-height: 1.5;
}

.bo-empty__action {
    margin-top: 1.25rem;
}

.bo-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    border-radius: 0.75rem;
    padding: 0.6rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.bo-btn:disabled {
    pointer-events: none;
    opacity: 0.55;
}

.bo-btn__icon {
    font-size: 0.85rem;
}

.bo-btn__icon--excel {
    color: #059669;
}

.bo-btn--export {
    border: 1px solid rgba(16, 185, 129, 0.28);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    color: #052e1f;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.bo-btn--export:hover {
    border-color: rgba(16, 185, 129, 0.45);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.12);
}

.bo-btn--edit {
    border: 1px solid transparent;
    background: linear-gradient(145deg, #d97706, #b45309);
    color: #fff;
    box-shadow: 0 4px 12px rgba(180, 83, 9, 0.22);
}

.bo-btn--edit:hover {
    filter: brightness(1.05);
}

.bo-btn--ghost {
    background: #ffffff;
    color: #44403c;
    border: 1px solid var(--bo-line);
}

.bo-btn--ghost:hover {
    background: #fafaf9;
}

.bo-input {
    width: 100%;
    border: 1px solid #d6d3d1;
    border-radius: 0.65rem;
    padding: 0.55rem 0.8rem;
    font-size: 0.875rem;
    background: #ffffff;
    color: var(--bo-ink);
}

.bo-input:focus {
    outline: none;
    border-color: #d97706;
    box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.15);
}

.bo-form__header {
    padding: 1.25rem 1.5rem 0.85rem;
    border-bottom: 1px solid #f5f5f4;
}

.bo-form__body {
    padding: 1.25rem 1.5rem;
}

.bo-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid #f5f5f4;
}

.bo-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #44403c;
    margin-bottom: 0.35rem;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
